import { supabase } from './supabase';

// Definição de tipos que funcionarão tanto no modelo atual quanto no futuro
export interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
  requires?: string[]; // Permissões que esta depende
  context?: 'global' | 'project' | 'organization';
  level?: 'basic' | 'advanced' | 'admin';
}

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  organization_id?: string;
  is_system: boolean;
  is_custom: boolean;
  parent_role_id?: string;
  valid_until?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface UserPermissions {
  userId: number;
  roleId: number;
  permissions: string[];
}

export interface ResourcePermission {
  id: string;
  user_id: string;
  resource_type: 'project' | 'chat_room' | 'report' | 'dashboard';
  resource_id: string;
  permission: string;
  granted_by: string;
  expires_at?: string;
  created_at: string;
}

export interface PermissionTemplate {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  category: 'financial' | 'project_management' | 'client_management' | 'analytics' | 'custom';
  is_system: boolean;
}

export interface PermissionAuditLog {
  id: string;
  user_id: string;
  target_user_id?: string;
  action: 'grant' | 'revoke' | 'role_change' | 'role_create' | 'role_delete';
  permission_id?: string;
  role_id?: string;
  resource_type?: string;
  resource_id?: string;
  reason?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

// Definição dos perfis de usuário e suas permissões
const ROLE_PERMISSIONS: Record<number, string[]> = {
  // Perfil Admin (ID 1) - Todas as permissões
  1: [
    // Time tracking
    'time.track', 'time.edit_own', 'time.edit_all', 'time.view_own', 'time.view_all',
    // Project management
    'project.view', 'project.create', 'project.edit', 'project.delete', 'project.archive',
    // Task management
    'task.view', 'task.create', 'task.edit_own', 'task.edit_all', 'task.assign', 'task.delete',
    // Team management
    'team.view', 'team.manage', 'team.invite', 'team.remove',
    // Finance
    'finance.view_own', 'finance.view_all', 'finance.reconcile', 'finance.export', 'finance.manage_accounts',
    // Chat & Communication
    'chat.create_rooms', 'chat.moderate', 'chat.archive', 'chat.delete_messages', 'chat.manage_members',
    // Analytics
    'analytics.view_dashboards', 'analytics.export_data', 'analytics.create_reports', 'analytics.manage_kpis',
    // Integration
    'integration.api_access', 'integration.webhook_manage', 'integration.export_csv', 'integration.external_sync',
    // System settings
    'settings.view', 'settings.manage', 'settings.user_management', 'settings.permissions'
  ],
  
  // Perfil Arquiteto (ID 2)
  2: [
    'time.track', 'time.edit_own', 'time.view_own',
    'project.view', 'project.edit',
    'task.view', 'task.create', 'task.edit_own',
    'team.view',
    'chat.create_rooms',
    'analytics.view_dashboards'
  ],
  
  // Perfil Gerente Financeiro (ID 3)
  3: [
    'time.view_all',
    'project.view',
    'finance.view_all', 'finance.reconcile', 'finance.export', 'finance.manage_accounts',
    'analytics.view_dashboards', 'analytics.export_data', 'analytics.create_reports',
    'integration.export_csv',
    'team.view'
  ],
  
  // Perfil Coordenador de Projetos (ID 4)
  4: [
    'time.view_all',
    'project.view', 'project.create', 'project.edit',
    'task.view', 'task.create', 'task.edit_all', 'task.assign',
    'team.view', 'team.manage',
    'chat.create_rooms', 'chat.moderate', 'chat.manage_members',
    'analytics.view_dashboards'
  ],
  
  // Perfil Analista (ID 5)
  5: [
    'time.view_own',
    'project.view',
    'task.view', 'task.create', 'task.edit_own',
    'team.view',
    'analytics.view_dashboards', 'analytics.export_data',
    'integration.export_csv'
  ],
  
};

// Definição das categorias e permissões disponíveis
export const PERMISSIONS: Permission[] = [
  // Time tracking permissions
  { id: 'time.track', name: 'Registrar tempo', description: 'Pode registrar tempo em projetos', category: 'Controle de Tempo', context: 'global', level: 'basic' },
  { id: 'time.edit_own', name: 'Editar próprios registros', description: 'Pode editar seus próprios registros de tempo', category: 'Controle de Tempo', context: 'global', level: 'basic' },
  { id: 'time.edit_all', name: 'Editar todos registros', description: 'Pode editar registros de tempo de qualquer pessoa', category: 'Controle de Tempo', context: 'global', level: 'admin' },
  { id: 'time.view_own', name: 'Ver próprios registros', description: 'Pode ver seus próprios registros de tempo', category: 'Controle de Tempo', context: 'global', level: 'basic' },
  { id: 'time.view_all', name: 'Ver todos registros', description: 'Pode ver registros de tempo de qualquer pessoa', category: 'Controle de Tempo', context: 'global', level: 'advanced' },
  
  // Project permissions
  { id: 'project.view', name: 'Ver projetos', description: 'Pode visualizar projetos', category: 'Projetos', context: 'global', level: 'basic' },
  { id: 'project.create', name: 'Criar projetos', description: 'Pode criar novos projetos', category: 'Projetos', context: 'global', level: 'advanced' },
  { id: 'project.edit', name: 'Editar projetos', description: 'Pode editar projetos existentes', category: 'Projetos', context: 'project', level: 'advanced' },
  { id: 'project.delete', name: 'Excluir projetos', description: 'Pode excluir projetos', category: 'Projetos', context: 'global', level: 'admin' },
  { id: 'project.archive', name: 'Arquivar projetos', description: 'Pode arquivar projetos finalizados', category: 'Projetos', context: 'project', level: 'advanced' },
  
  // Task permissions
  { id: 'task.view', name: 'Ver tarefas', description: 'Pode visualizar tarefas', category: 'Tarefas', context: 'global', level: 'basic' },
  { id: 'task.create', name: 'Criar tarefas', description: 'Pode criar novas tarefas', category: 'Tarefas', context: 'project', level: 'basic' },
  { id: 'task.edit_own', name: 'Editar próprias tarefas', description: 'Pode editar suas próprias tarefas', category: 'Tarefas', context: 'global', level: 'basic' },
  { id: 'task.edit_all', name: 'Editar todas tarefas', description: 'Pode editar tarefas de qualquer pessoa', category: 'Tarefas', context: 'global', level: 'advanced' },
  { id: 'task.assign', name: 'Atribuir tarefas', description: 'Pode atribuir tarefas a outros usuários', category: 'Tarefas', context: 'project', level: 'advanced' },
  { id: 'task.delete', name: 'Excluir tarefas', description: 'Pode excluir tarefas', category: 'Tarefas', context: 'project', level: 'admin' },
  
  // Team permissions
  { id: 'team.view', name: 'Ver equipe', description: 'Pode visualizar membros da equipe', category: 'Equipe', context: 'global', level: 'basic' },
  { id: 'team.manage', name: 'Gerenciar equipe', description: 'Pode gerenciar membros da equipe', category: 'Equipe', context: 'organization', level: 'advanced' },
  { id: 'team.invite', name: 'Convidar membros', description: 'Pode enviar convites para novos membros', category: 'Equipe', context: 'organization', level: 'advanced' },
  { id: 'team.remove', name: 'Remover membros', description: 'Pode remover membros da organização', category: 'Equipe', context: 'organization', level: 'admin' },
  
  // Finance permissions
  { id: 'finance.view_own', name: 'Ver próprios dados financeiros', description: 'Pode visualizar seus próprios dados financeiros', category: 'Financeiro', context: 'global', level: 'basic' },
  { id: 'finance.view_all', name: 'Ver todos dados financeiros', description: 'Pode visualizar todos os dados financeiros', category: 'Financeiro', context: 'organization', level: 'advanced' },
  { id: 'finance.reconcile', name: 'Conciliação bancária', description: 'Pode realizar conciliação bancária', category: 'Financeiro', context: 'organization', level: 'advanced' },
  { id: 'finance.export', name: 'Exportar relatórios financeiros', description: 'Pode exportar relatórios financeiros', category: 'Financeiro', context: 'organization', level: 'advanced' },
  { id: 'finance.manage_accounts', name: 'Gerenciar contas bancárias', description: 'Pode criar e gerenciar contas bancárias', category: 'Financeiro', context: 'organization', level: 'admin' },
  
  // Chat & Communication permissions
  { id: 'chat.create_rooms', name: 'Criar salas de chat', description: 'Pode criar novas salas de chat', category: 'Comunicação', context: 'organization', level: 'basic' },
  { id: 'chat.moderate', name: 'Moderar conversas', description: 'Pode moderar conversas e aplicar sanções', category: 'Comunicação', context: 'global', level: 'advanced' },
  { id: 'chat.archive', name: 'Arquivar mensagens', description: 'Pode arquivar mensagens e conversas', category: 'Comunicação', context: 'global', level: 'advanced' },
  { id: 'chat.delete_messages', name: 'Excluir mensagens', description: 'Pode excluir mensagens de outros usuários', category: 'Comunicação', context: 'global', level: 'admin' },
  { id: 'chat.manage_members', name: 'Gerenciar membros do chat', description: 'Pode adicionar e remover membros de salas de chat', category: 'Comunicação', context: 'global', level: 'advanced' },
  
  // Analytics permissions
  { id: 'analytics.view_dashboards', name: 'Ver dashboards', description: 'Pode visualizar dashboards analíticos', category: 'Analytics', context: 'global', level: 'basic' },
  { id: 'analytics.export_data', name: 'Exportar dados analíticos', description: 'Pode exportar dados de relatórios e dashboards', category: 'Analytics', context: 'organization', level: 'advanced' },
  { id: 'analytics.create_reports', name: 'Criar relatórios customizados', description: 'Pode criar relatórios personalizados', category: 'Analytics', context: 'organization', level: 'advanced' },
  { id: 'analytics.manage_kpis', name: 'Gerenciar KPIs', description: 'Pode definir e gerenciar indicadores de performance', category: 'Analytics', context: 'organization', level: 'admin' },
  
  // Integration permissions
  { id: 'integration.api_access', name: 'Acesso à API', description: 'Pode acessar APIs do sistema', category: 'Integrações', context: 'organization', level: 'advanced' },
  { id: 'integration.webhook_manage', name: 'Gerenciar webhooks', description: 'Pode criar e gerenciar webhooks', category: 'Integrações', context: 'organization', level: 'admin' },
  { id: 'integration.export_csv', name: 'Exportar dados CSV/PDF', description: 'Pode exportar dados em formatos CSV e PDF', category: 'Integrações', context: 'global', level: 'basic' },
  { id: 'integration.external_sync', name: 'Sincronização externa', description: 'Pode configurar sincronização com sistemas externos', category: 'Integrações', context: 'organization', level: 'admin' },
  
  // Advanced Settings permissions
  { id: 'settings.view', name: 'Ver configurações', description: 'Pode visualizar configurações do sistema', category: 'Configurações', context: 'global', level: 'basic' },
  { id: 'settings.manage', name: 'Gerenciar configurações básicas', description: 'Pode alterar configurações básicas do sistema', category: 'Configurações', context: 'organization', level: 'advanced' },
  { id: 'settings.user_management', name: 'Gerenciamento de usuários', description: 'Pode gerenciar usuários e seus acessos', category: 'Configurações', context: 'organization', level: 'admin' },
  { id: 'settings.permissions', name: 'Gerenciar permissões', description: 'Pode gerenciar roles e permissões do sistema', category: 'Configurações', context: 'organization', level: 'admin' }
];

// Funções que podem ser adaptadas para o modelo futuro

/**
 * Verifica se um usuário tem uma permissão específica
 */
export async function hasPermission(userId: number, permissionId: string): Promise<boolean> {
  try {
    // Temporariamente desabilitado - usar apenas fallback
    const enhancedError = 'Usando fallback';
    
    // Fallback para o sistema simplificado
    const { data, error } = await supabase
      .from('user_profile')
      .select('user_role_id')
      .eq('id', userId)
      .single();
    
    if (error || !data) {
      console.error('Erro ao verificar permissão:', error);
      return false;
    }

    const roleId = data.user_role_id;
    if (!roleId || !ROLE_PERMISSIONS[roleId]) {
      return false;
    }

    // Verificar se a permissão está na lista de permissões do perfil
    return ROLE_PERMISSIONS[roleId].includes(permissionId);
  } catch (err) {
    console.error('Erro ao verificar permissão:', err);
    return false;
  }
}

/**
 * Obtém todas as permissões de um usuário
 */
export async function getUserPermissions(userId: number): Promise<string[]> {
  try {
    // Primeiro tenta buscar do sistema evoluído com tratamento de erro
    let roleData = null;
    let roleError = null;
    
    try {
      // Temporariamente desabilitado para usar fallback
      throw new Error('Usando sistema de fallback');
      
      const result = await supabase
        .from('user_role_assignments')
        .select(`
          role_id,
          user_roles!inner (
            id,
            name,
            role_permissions!inner (
              user_permissions!inner (
                name
              )
            )
          )
        `)
        .eq('user_id', userId.toString());
      
      roleData = result.data;
      roleError = result.error;
    } catch (err) {
      console.log('Sistema de permissões evoluído não disponível, usando fallback');
      roleError = err;
    }
    
    if (!roleError && roleData && roleData.length > 0) {
      const permissions = new Set<string>();
      
      roleData.forEach(assignment => {
        const role = assignment.user_roles as any;
        if (role?.role_permissions) {
          role.role_permissions.forEach((rp: any) => {
            if (rp.user_permissions?.name) {
              permissions.add(rp.user_permissions.name);
            }
          });
        }
      });
      
      // Também buscar permissões de recursos específicos
      const { data: resourcePerms, error: resourceError } = await supabase
        .from('resource_permissions')
        .select('permission')
        .eq('user_id', userId.toString())
        .or('expires_at.is.null,expires_at.gt.' + new Date().toISOString());
      
      if (!resourceError && resourcePerms) {
        resourcePerms.forEach(rp => permissions.add(rp.permission));
      }
      
      return Array.from(permissions);
    }
    
    // Fallback para o sistema simplificado
    const { data, error } = await supabase
      .from('user_profile')
      .select('user_role_id')
      .eq('id', userId)
      .single();
    
    if (error || !data) {
      console.error('Erro ao obter permissões:', error);
      return [];
    }

    const roleId = data.user_role_id;
    if (!roleId || !ROLE_PERMISSIONS[roleId]) {
      return [];
    }

    return ROLE_PERMISSIONS[roleId];
  } catch (err) {
    console.error('Erro ao obter permissões:', err);
    return [];
  }
}

/**
 * Obtém os perfis disponíveis
 */
export async function getRoles(): Promise<Role[]> {
  try {
    // Buscar roles do banco de dados
    const { data: dbRoles, error } = await supabase
      .from('user_roles')
      .select(`
        *,
        role_permissions!inner (
          user_permissions!inner (
            name
          )
        )
      `);
    
    if (!error && dbRoles && dbRoles.length > 0) {
      return dbRoles.map(role => ({
        id: role.id,
        name: role.name,
        description: role.description || '',
        permissions: role.role_permissions?.map((rp: any) => rp.user_permissions.name) || [],
        organization_id: role.organization_id,
        is_system: role.is_system || false,
        is_custom: role.is_custom || false,
        parent_role_id: role.parent_role_id,
        valid_until: role.valid_until,
        created_by: role.created_by,
        created_at: role.created_at,
        updated_at: role.updated_at
      }));
    }
    
    // Fallback para roles predefinidos
    return [
      { 
        id: '1', 
        name: 'Admin',
        description: 'Acesso total ao sistema',
        permissions: ROLE_PERMISSIONS[1],
        is_system: true,
        is_custom: false
      },
      {
        id: '2',
        name: 'Arquiteto Senior',
        description: 'Acesso completo para arquitetos sênior',
        permissions: ROLE_PERMISSIONS[2],
        is_system: true,
        is_custom: false
      },
      {
        id: '3',
        name: 'Gerente Financeiro',
        description: 'Acesso completo ao módulo financeiro',
        permissions: ROLE_PERMISSIONS[3],
        is_system: true,
        is_custom: false
      },
      {
        id: '4',
        name: 'Coordenador de Projetos',
        description: 'Gerenciamento de projetos e equipes',
        permissions: ROLE_PERMISSIONS[4],
        is_system: true,
        is_custom: false
      },
      {
        id: '5',
        name: 'Analista',
        description: 'Acesso básico com foco em análise',
        permissions: ROLE_PERMISSIONS[5],
        is_system: true,
        is_custom: false
      },
      {
        id: '6',
        name: 'Arquiteto Junior',
        description: 'Acesso básico para arquitetos em início de carreira',
        permissions: ROLE_PERMISSIONS[6],
        is_system: true,
        is_custom: false
      }
    ];
  } catch (err) {
    console.error('Erro ao obter roles:', err);
    return [];
  }
};

/* Implementação futura com tabelas relacionais:
export async function getUserRoles() {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('*');
    
    if (error) throw error;
    
    return data || [];
  } catch (err) {
    console.error('Erro ao obter perfis:', err);
    return [];
  }
}
*/

/**
 * Obtém todas as permissões disponíveis
 */
export function getPermissions(): Permission[] {
  return PERMISSIONS;
}

/**
 * Atualiza o perfil de um usuário
 */
export async function updateUserRole(userId: string, roleId: string, organizationId: string): Promise<boolean> {
  try {
    // Primeiro tenta usar o sistema evoluído
    const { error: enhancedError } = await supabase
      .from('user_role_assignments')
      .upsert({ 
        user_id: userId,
        role_id: roleId,
        organization_id: organizationId
      });
    
    if (!enhancedError) {
      // Log da ação
      await logPermissionAction({
        user_id: userId,
        action: 'role_change',
        role_id: roleId,
        metadata: { organization_id: organizationId }
      });
      return true;
    }
    
    // Fallback para o sistema antigo
    const { error } = await supabase
      .from('user_profile')
      .update({ user_role_id: parseInt(roleId) })
      .eq('id', parseInt(userId));
    
    if (error) {
      console.error('Erro ao atualizar perfil:', error);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error('Erro ao atualizar perfil:', err);
    return false;
  }
}

/**
 * Atualiza as permissões de um perfil
 */
export async function updateRolePermissions(roleId: string, permissions: string[]): Promise<boolean> {
  try {
    // Remove permissões existentes
    const { error: deleteError } = await supabase
      .from('role_permissions')
      .delete()
      .eq('role_id', roleId);
    
    if (deleteError) {
      console.error('Erro ao remover permissões existentes:', deleteError);
      return false;
    }
    
    // Buscar IDs das permissões pelo nome
    const { data: permissionData, error: permError } = await supabase
      .from('user_permissions')
      .select('id, name')
      .in('name', permissions);
    
    if (permError || !permissionData) {
      console.error('Erro ao buscar permissões:', permError);
      return false;
    }
    
    // Insere novas permissões
    if (permissionData.length > 0) {
      const permissionRecords = permissionData.map(perm => ({
        role_id: roleId,
        permission_id: perm.id
      }));
      
      const { error: insertError } = await supabase
        .from('role_permissions')
        .insert(permissionRecords);
      
      if (insertError) {
        console.error('Erro ao inserir novas permissões:', insertError);
        return false;
      }
    }
    
    // Log da ação
    await logPermissionAction({
      user_id: 'system', // TODO: passar o usuário real
      action: 'role_create',
      role_id: roleId,
      metadata: { permissions_updated: permissions }
    });
    
    return true;
  } catch (err) {
    console.error('Erro ao atualizar permissões do perfil:', err);
    return false;
  }
}

// Templates de roles predefinidos
export const ROLE_TEMPLATES: Record<string, PermissionTemplate> = {
  financial_analyst: {
    id: 'financial_analyst',
    name: 'Analista Financeiro',
    description: 'Perfil especializado em análise financeira e relatórios',
    permissions: [
      'finance.view_all', 'finance.export', 'analytics.view_dashboards', 
      'analytics.export_data', 'analytics.create_reports', 'integration.export_csv',
      'project.view', 'time.view_all'
    ],
    category: 'financial',
    is_system: true
  },
  project_coordinator: {
    id: 'project_coordinator',
    name: 'Coordenador de Projetos',
    description: 'Gerenciamento completo de projetos e equipes',
    permissions: [
      'project.view', 'project.create', 'project.edit', 'project.archive',
      'task.view', 'task.create', 'task.edit_all', 'task.assign',
      'team.view', 'team.manage', 'time.view_all',
      'chat.create_rooms', 'chat.moderate', 'chat.manage_members',
      'analytics.view_dashboards'
    ],
    category: 'project_management',
    is_system: true
  },
  client_manager: {
    id: 'client_manager',
    name: 'Gerente de Contas',
    description: 'Gerenciamento de relacionamento com clientes',
    permissions: [
      'project.view', 'task.view', 'time.view_all',
      'chat.create_rooms', 'chat.manage_members',
      'analytics.view_dashboards', 'analytics.export_data',
      'integration.export_csv', 'team.view'
    ],
    category: 'client_management',
    is_system: true
  },
  data_analyst: {
    id: 'data_analyst',
    name: 'Analista de Dados',
    description: 'Especialista em análise de dados e relatórios',
    permissions: [
      'analytics.view_dashboards', 'analytics.export_data', 'analytics.create_reports',
      'integration.export_csv', 'integration.api_access',
      'project.view', 'task.view', 'time.view_all', 'finance.view_all'
    ],
    category: 'analytics',
    is_system: true
  },
  architect_senior: {
    id: 'architect_senior',
    name: 'Arquiteto Senior',
    description: 'Arquiteto sênior com capacidades de liderança e gerenciamento',
    permissions: [
      'time.track', 'time.edit_own', 'time.view_own', 'time.view_all',
      'project.view', 'project.create', 'project.edit', 'project.archive',
      'task.view', 'task.create', 'task.edit_own', 'task.edit_all', 'task.assign',
      'team.view', 'team.manage',
      'chat.create_rooms', 'chat.manage_members',
      'analytics.view_dashboards', 'analytics.export_data',
      'integration.export_csv'
    ],
    category: 'project_management',
    is_system: true
  },
  architect_junior: {
    id: 'architect_junior',
    name: 'Arquiteto Junior',
    description: 'Arquiteto júnior com acesso básico para desenvolvimento',
    permissions: [
      'time.track', 'time.edit_own', 'time.view_own',
      'project.view',
      'task.view', 'task.create', 'task.edit_own',
      'team.view',
      'analytics.view_dashboards',
      'integration.export_csv'
    ],
    category: 'project_management',
    is_system: true
  }
};

/**
 * Obtém todos os templates de roles disponíveis
 */
export function getRoleTemplates(): PermissionTemplate[] {
  return Object.values(ROLE_TEMPLATES);
}

/**
 * Cria um role customizado baseado em um template
 */
export async function createRoleFromTemplate(
  templateId: string, 
  customName: string, 
  customDescription: string, 
  organizationId: string,
  createdBy: string
): Promise<Role | null> {
  try {
    const template = ROLE_TEMPLATES[templateId];
    if (!template) {
      console.error('Template não encontrado:', templateId);
      return null;
    }

    // Criar role no banco de dados
    const { data: roleData, error: roleError } = await supabase
      .from('user_roles')
      .insert({
        organization_id: organizationId,
        name: customName,
        description: customDescription,
        is_system: false,
        is_custom: true,
        created_by: createdBy
      })
      .select()
      .single();

    if (roleError || !roleData) {
      console.error('Erro ao criar role:', roleError);
      return null;
    }

    // Buscar IDs das permissões
    const { data: permissionData, error: permError } = await supabase
      .from('user_permissions')
      .select('id, name')
      .eq('organization_id', organizationId)
      .in('name', template.permissions);

    if (!permError && permissionData && permissionData.length > 0) {
      // Associar permissões ao role
      const rolePermissions = permissionData.map(perm => ({
        role_id: roleData.id,
        permission_id: perm.id
      }));

      await supabase
        .from('role_permissions')
        .insert(rolePermissions);
    }

    const newRole: Role = {
      id: roleData.id,
      name: customName,
      description: customDescription,
      permissions: [...template.permissions],
      organization_id: organizationId,
      is_system: false,
      is_custom: true,
      created_by: createdBy,
      created_at: roleData.created_at
    };

    // Log da ação
    await logPermissionAction({
      user_id: createdBy,
      action: 'role_create',
      role_id: newRole.id,
      metadata: { template_used: templateId, role_name: customName }
    });

    return newRole;
  } catch (err) {
    console.error('Erro ao criar role do template:', err);
    return null;
  }
}

/**
 * Verifica permissões de nível de recurso
 */
export async function hasResourcePermission(
  userId: number, 
  permissionId: string, 
  resourceType: string, 
  resourceId: string
): Promise<boolean> {
  try {
    // Primeiro verificar permissão global
    const hasGlobal = await hasPermission(userId, permissionId);
    if (hasGlobal) return true;

    // Verificar permissão específica do recurso no banco
    const { data, error } = await supabase
      .from('resource_permissions')
      .select('id')
      .eq('user_id', userId.toString())
      .eq('permission', permissionId)
      .eq('resource_type', resourceType)
      .eq('resource_id', resourceId)
      .or('expires_at.is.null,expires_at.gt.' + new Date().toISOString())
      .maybeSingle();
    
    if (error) {
      console.error('Erro ao verificar permissão de recurso:', error);
      return false;
    }
    
    return !!data;
  } catch (err) {
    console.error('Erro ao verificar permissão de recurso:', err);
    return false;
  }
}

/**
 * Concede permissão temporária de recurso
 */
export async function grantResourcePermission(
  userId: string,
  permissionId: string,
  resourceType: string,
  resourceId: string,
  grantedBy: string,
  expiresAt?: string,
  reason?: string
): Promise<boolean> {
  try {
    // Inserir no banco de dados
    const { error } = await supabase
      .from('resource_permissions')
      .insert({
        user_id: userId,
        resource_type: resourceType,
        resource_id: resourceId,
        permission: permissionId,
        granted_by: grantedBy,
        expires_at: expiresAt,
        reason
      });

    if (error) {
      console.error('Erro ao conceder permissão de recurso:', error);
      return false;
    }
    
    // Log da ação
    await logPermissionAction({
      user_id: grantedBy,
      target_user_id: userId,
      action: 'grant',
      permission_id: permissionId,
      resource_type: resourceType,
      resource_id: resourceId,
      reason,
      metadata: { expires_at: expiresAt }
    });

    return true;
  } catch (err) {
    console.error('Erro ao conceder permissão de recurso:', err);
    return false;
  }
}

/**
 * Sistema de log de auditoria para permissões
 */
export async function logPermissionAction(
  action: Omit<PermissionAuditLog, 'id' | 'created_at'>
): Promise<void> {
  try {
    // Inserir no banco de dados
    const { error } = await supabase
      .from('permission_audit_logs')
      .insert({
        user_id: action.user_id,
        target_user_id: action.target_user_id,
        action: action.action,
        permission_id: action.permission_id,
        role_id: action.role_id,
        resource_type: action.resource_type,
        resource_id: action.resource_id,
        reason: action.reason,
        metadata: action.metadata || {}
      });

    if (error) {
      console.error('Erro ao registrar log de auditoria:', error);
      
      // Fallback para localStorage em caso de erro
      const auditLog: PermissionAuditLog = {
        id: `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        ...action,
        created_at: new Date().toISOString()
      };
      
      const existingLogs = JSON.parse(localStorage.getItem('permission_audit_logs') || '[]');
      existingLogs.unshift(auditLog);
      if (existingLogs.length > 1000) {
        existingLogs.splice(1000);
      }
      localStorage.setItem('permission_audit_logs', JSON.stringify(existingLogs));
    }
  } catch (err) {
    console.error('Erro ao registrar log de auditoria:', err);
  }
}

/**
 * Obtém logs de auditoria de permissões
 */
export async function getPermissionAuditLogs(limit: number = 50): Promise<PermissionAuditLog[]> {
  try {
    // Buscar do banco de dados
    const { data, error } = await supabase
      .from('permission_audit_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (!error && data) {
      return data.map(log => ({
        id: log.id,
        user_id: log.user_id,
        target_user_id: log.target_user_id,
        action: log.action,
        permission_id: log.permission_id,
        role_id: log.role_id,
        resource_type: log.resource_type,
        resource_id: log.resource_id,
        reason: log.reason,
        metadata: log.metadata || {},
        created_at: log.created_at
      }));
    }

    // Fallback para localStorage
    const logs = JSON.parse(localStorage.getItem('permission_audit_logs') || '[]');
    return logs.slice(0, limit);
  } catch (err) {
    console.error('Erro ao obter logs de auditoria:', err);
    return [];
  }
}

/**
 * Verifica permissões condicionais (tempo, contexto, etc.)
 */
export async function hasConditionalPermission(
  userId: number,
  permissionId: string,
  context?: {
    time?: { start: string; end: string };
    location?: string;
    device?: string;
    ip?: string;
  }
): Promise<boolean> {
  // Verificar permissão básica primeiro
  const hasBasic = await hasPermission(userId, permissionId);
  if (!hasBasic) return false;

  if (!context) return true;

  // Verificar restrições de tempo
  if (context.time) {
    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5); // HH:MM
    
    if (currentTime < context.time.start || currentTime > context.time.end) {
      console.log('Acesso negado: fora do horário permitido');
      return false;
    }
  }

  // Outras verificações condicionais podem ser adicionadas aqui
  // location, device, ip, etc.

  return true;
}

/**
 * Delegação temporária de permissões
 */
export async function delegatePermissions(
  fromUserId: string,
  toUserId: string,
  permissions: string[],
  expiresAt: string,
  reason: string
): Promise<boolean> {
  try {
    // Verificar se o usuário que delega tem as permissões
    for (const permission of permissions) {
      const hasPermissionToDelegate = await hasPermission(parseInt(fromUserId), permission);
      if (!hasPermissionToDelegate) {
        console.error(`Usuário ${fromUserId} não tem permissão para delegar: ${permission}`);
        return false;
      }
    }

    // Criar delegações temporárias
    for (const permission of permissions) {
      await grantResourcePermission(
        toUserId,
        permission,
        'delegation',
        fromUserId,
        fromUserId,
        expiresAt,
        reason
      );
    }

    // Log da delegação
    await logPermissionAction({
      user_id: fromUserId,
      target_user_id: toUserId,
      action: 'grant',
      metadata: { 
        delegation: true, 
        permissions, 
        expires_at: expiresAt, 
        reason 
      }
    });

    console.log('Permissões delegadas com sucesso');
    return true;
  } catch (err) {
    console.error('Erro ao delegar permissões:', err);
    return false;
  }
}

/**
 * Hook de contexto de permissão que pode ser usado em componentes
 */
export function usePermissions(userId: number) {
  // Esta função poderia ser expandida para um hook React completo
  return {
    check: (permission: string) => hasPermission(userId, permission),
    checkResource: (permission: string, resourceType: string, resourceId: string) => 
      hasResourcePermission(userId, permission, resourceType, resourceId),
    checkConditional: (permission: string, context?: any) => 
      hasConditionalPermission(userId, permission, context),
    getAll: () => getUserPermissions(userId),
    getAuditLogs: () => getPermissionAuditLogs(),
    delegate: (toUserId: string, permissions: string[], expiresAt: string, reason: string) =>
      delegatePermissions(userId.toString(), toUserId, permissions, expiresAt, reason)
  };
}